import { QueryList, ElementRef } from '@angular/core';

import {
  healthWellnessCheckCodes,
  healthWellnessTranslateCodes,
  tierValues,
  networkTypeCodes,
} from './patient.constants';

import {
  Accumulation,
  accumulationOONInit,
  accumulationPreferredInit,
  PharmacyBenefit,
  TierBenefit,
  Variation,
  AccumulationNetwork,
  DentalPlanService,
  accumulationINNInit,
  TableHeader,
} from './patient.model';

import flatten from 'lodash/flatten';
import { claimSystemCodes } from '@cigna/chcp/shared/util';

const networkOrder = [
  networkTypeCodes.preferred,
  networkTypeCodes.INN,
  networkTypeCodes.OON,
];

export function getBenefitCodes(
  claimSystemCode: string,
  clinicalExpansion: string | null,
): string[] {
  const resFormatted = clinicalExpansion?.match(/.{0,2}/g) || [];
  return resFormatted.filter(
    (code: string) =>
      (claimSystemCode !== claimSystemCodes.Facets &&
        healthWellnessCheckCodes.includes(code)) ||
      (!healthWellnessCheckCodes.includes(code) &&
        healthWellnessTranslateCodes.includes(code)),
  );
}

export function setInOutHeaders(
  list: string[],
  values: Accumulation,
  title?: string,
): TableHeader[][] {
  const filteredList = list.filter((item, i, ar) => ar.indexOf(item) === i);
  const header1: TableHeader[] = [
    {
      key: title || '',
      width: 25,
    },
  ];
  const header2: any[] = [
    {
      key: '',
      width: 25,
    },
  ];

  filteredList.forEach((item) => {
    header1.push({
      key: item,
      colspan: 3,
      width: 75 / filteredList.length,
    });
    const array = ['amount', 'met', 'remaining'];
    array.forEach((element) => {
      if (element in values) {
        header2.push({
          key: element,
          colspan: 1,
        });
      }
    });
  });
  return [header1, header2];
}

export function sortArray(array: any, order: string[], key?: string) {
  return array.sort((a: any, b: any) => {
    if (key) {
      return order.indexOf(a[key]) - order.indexOf(b[key]);
    }
    return order.indexOf(a) - order.indexOf(b);
  });
}

export function groupByVariation(
  accumulations: Accumulation[] | undefined,
  networkTypes?: string[],
): Accumulation[] {
  /* groups the data based on 'desc', 'coveredServices', 'cignaCareNetwork' and 'covers' and returns array of values*/
  const values: { [key: string]: Accumulation[] } = {};
  accumulations?.forEach((accum: Accumulation) => {
    const accumId =
      accum.desc +
      accum.coveredServices.toString() +
      accum.cignaCareNetwork +
      (!accum.tier || accum.tier === tierValues.TIER5 ? '' : accum.tier);

    // TODO: resolve CHCP disables by bot
    // eslint-disable-next-line no-unused-expressions
    values[accumId] ? values[accumId].push(accum) : (values[accumId] = [accum]);
  });

  // TODO: resolve CHCP disables by bot
  // eslint-disable-next-line sonarjs/cognitive-complexity
  return Object.values(values).map((accumulation: Accumulation[]) => {
    const updatedValues = networkTypes
      ? accumulation.length >= networkTypes.length
        ? accumulation
        : networkTypes.map((network) => {
            const data =
              network === 'INN'
                ? accumulationINNInit
                : network === 'OON'
                ? accumulationOONInit
                : accumulationPreferredInit;
            const accumData = accumulation.filter(
              (accum) => accum.networkType === network,
            );
            return accumData
              ? accumData
              : {
                  ...data,
                  tier: accumulation[0].tier,
                  desc: accumulation[0].desc,
                  coveredServices: accumulation[0].coveredServices,
                  cignaCareNetwork: accumulation[0].cignaCareNetwork,
                };
          })
      : accumulation.length === 1 &&
        accumulation[0].networkType.toUpperCase() !== networkTypeCodes.indemnity
      ? [
          accumulation[0],
          {
            ...accumulationOONInit,
            tier: accumulation[0].tier,
            desc: accumulation[0].desc,
          },
        ]
      : accumulation;
    return sortArray(flatten(updatedValues), networkOrder, 'networkType');
  });
}

export function currencyToNumber(value: string): number {
  return value ? parseFloat(value.replace(/[^0-9.]/g, '')) : 0;
}

export function accordionUtilScrollToLegacy(
  element: ElementRef,
  blockRef: QueryList<any>,
  accordionName: string,
) {
  const accordionSection = element.nativeElement.children?.namedItem(
    accordionName,
  );

  if (accordionSection) {
    accordionSection.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    const blockElement = blockRef.find(
      (blockElem: any) =>
        accordionSection.textContent.trim() === blockElem.headerText,
    );

    if (blockElement) {
      blockElement.expanded = true;
    }
  }
}

export function accordionUtilScrollTo(
  element: ElementRef,
  blockRef: QueryList<any>,
  accordionName: string,
  childAccordions?: QueryList<any>,
) {
  const accordionSection =
    element.nativeElement.children?.namedItem(accordionName) ||
    element.nativeElement.firstElementChild?.children?.namedItem(accordionName);

  if (accordionSection) {
    accordionSection.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    const blockElement = blockRef.find(
      (br) =>
        br.headerText?.toUpperCase() ===
        accordionSection?.textContent?.toUpperCase(),
    );

    if (blockElement) {
      blockElement.open();
      if (childAccordions) {
        childAccordions.forEach((accordion) => {
          if (
            accordion.tag.toUpperCase() ===
            accordionSection?.textContent?.toUpperCase()
          ) {
            accordion.expandAllChildCollapsibles();
          }
        });
      }
    }
  }
}

export function accordionUtilExpandAll(collapseBlockRef: QueryList<any>) {
  collapseBlockRef.forEach((blockElem) => {
    blockElem.expanded = true;
  });
}

export function accordionUtilExpandSelected(
  collapseBlockRef: QueryList<any>,
  header: string,
) {
  const blockElement = collapseBlockRef.find(
    (blockRef) => blockRef.headerText?.toUpperCase() === header.toUpperCase(),
  );

  if (blockElement) {
    // Needed for non enhanced view's since they dont have access to .open() function
    if (typeof blockElement.open === 'function') {
      blockElement.open();
    } else {
      blockElement.expanded = true;
    }
  }
}

export function accordionUtilCollapseSelected(
  collapseBlockRef: QueryList<any>,
  header: string,
) {
  const blockElement = collapseBlockRef.find(
    (blockRef) => blockRef.headerText?.toUpperCase() === header.toUpperCase(),
  );
  if (blockElement) {
    blockElement.expanded = false;
  }
}

// To check if the selected Benefit is part of benefit reference array list and return the filtered items
export function refBenefitsValidation(
  refBenefits: string[] | null,
  selectedBenefit: string,
): boolean {
  return (
    !refBenefits ||
    !refBenefits?.length ||
    refBenefits.includes(selectedBenefit)
  );
}

export const isTierPlan5 = (pharmacyBenefit: PharmacyBenefit | null): boolean =>
  !!pharmacyBenefit && pharmacyBenefit.planType === '5';

export const isProgCodeAvailable = (
  progTypecodes: string | null,
  progType: string,
): boolean => {
  if (progTypecodes && progType) {
    return progTypecodes.split('_').includes(progType);
  }
  return false;
};

export const isPriorAuth = (pharmacyBenefit: PharmacyBenefit): boolean => {
  let isAuth = true;
  if (pharmacyBenefit) {
    if (
      !pharmacyBenefit.rxClinicalProgCodePA &&
      !isProgCodeAvailable(pharmacyBenefit.spltyProgTypeCodes, 'PA') &&
      !isProgCodeAvailable(pharmacyBenefit.nonSpltyProgTypeCodes, 'PA')
    ) {
      isAuth = false;
    }
  } else {
    isAuth = false;
  }
  return isAuth;
};

export const displayCodeMessage = (
  progTypecodes: string | null,
  progType: string,
): string => {
  let message = 'No';
  if (
    progTypecodes &&
    progType &&
    progTypecodes.split('_').includes(progType)
  ) {
    message = 'Yes';
  }
  return message;
};

export const displayProgTypeCodes = (
  pharmacyBenefit: PharmacyBenefit,
  progType: string,
): boolean =>
  !!pharmacyBenefit &&
  (isProgCodeAvailable(pharmacyBenefit.spltyProgTypeCodes, progType) ||
    isProgCodeAvailable(pharmacyBenefit.nonSpltyProgTypeCodes, progType));

export const isTheraCare = (pharmacyBenefit: PharmacyBenefit): boolean =>
  !!pharmacyBenefit && !!pharmacyBenefit.rxClinicalProgCodeTE;

export const getDrugListURL = (pharmacyBenefit: PharmacyBenefit): string => {
  let baseUrl = '/secure/gl/druglist/druglist.html?';
  if (pharmacyBenefit) {
    if (
      displayProgTypeCodes(pharmacyBenefit, 'QL') ||
      displayProgTypeCodes(pharmacyBenefit, 'PA') ||
      displayProgTypeCodes(pharmacyBenefit, 'ST')
    ) {
      baseUrl += pharmacyBenefit.drugListURL;
    } else {
      baseUrl += pharmacyBenefit.drugListURL
        ? `${pharmacyBenefit.drugListURL.split('=DrugList')[0]}=DrugList`
        : ' ';
    }
  }
  return baseUrl;
};

export const getRefillLimitFootNote = (eshdRetlFillLmt: any): string => {
  let message = '';
  if (!eshdRetlFillLmt) {
    return message;
  }
  const limit = parseInt(eshdRetlFillLmt, 10);
  message =
    limit === 0 ? 'limit.met' : limit > 0 && limit < 10 ? 'limit.left' : '';
  return message;
};

export const getFillDescription = (data: any): string => {
  let message = '';
  const fills30DayPermtdNum = parseInt(data, 10);
  switch (fills30DayPermtdNum) {
    case 1:
      message = 'one';
      break;
    case 2:
      message = 'two';
      break;
    case 3:
      message = 'three';
      break;
    case 4:
      message = 'four';
      break;
    case 5:
      message = 'five';
      break;
    case 6:
      message = 'six';
      break;
    case 7:
      message = 'seven';
      break;
    case 8:
      message = 'eight';
      break;
    case 9:
      message = 'nine';
      break;
  }
  return message;
};

export const showPharmacy = (
  pharmacyBenefit: PharmacyBenefit | null,
): boolean =>
  !!pharmacyBenefit &&
  (!!pharmacyBenefit.carveoutVendorName ||
    (pharmacyBenefit?.tierBenefitsList !== null &&
      pharmacyBenefit?.tierBenefitsList?.length > 0) ||
    (pharmacyBenefit?.planAmountList !== null &&
      pharmacyBenefit?.planAmountList?.length > 0));

export const isDaysEqual = (day1: string, day2: string): boolean =>
  !!day1 && !!day2 && day1.toUpperCase() === day2.toUpperCase();

export const isDaySupplySame = (pharmacyBenefit: PharmacyBenefit): boolean => {
  let isSameDaySupply = false;
  let isRetailExist = false;
  let isMailOrderExist = false;
  if (
    pharmacyBenefit &&
    pharmacyBenefit.tierBenefitsList &&
    pharmacyBenefit.tierBenefitsList.length > 0
  ) {
    let tierBenefits: TierBenefit[] = pharmacyBenefit.tierBenefitsList.filter(
      (data) =>
        data && data.tierType && data.tierType.toUpperCase().startsWith('R'),
    );
    isRetailExist = tierBenefits && tierBenefits.length > 0;
    tierBenefits = pharmacyBenefit.tierBenefitsList.filter(
      (data) =>
        data && data.tierType && data.tierType.toUpperCase().startsWith('M'),
    );
    isMailOrderExist = tierBenefits && tierBenefits.length > 0;

    if (
      isRetailExist &&
      isMailOrderExist &&
      pharmacyBenefit.retailSupplyDays &&
      pharmacyBenefit.retailSpecialityDrugDays &&
      pharmacyBenefit.mailOrderSupplyDays &&
      pharmacyBenefit.mailOrderSpecialityDrugDays
    ) {
      isSameDaySupply =
        isDaysEqual(
          pharmacyBenefit.retailSupplyDays,
          pharmacyBenefit.retailSpecialityDrugDays,
        ) &&
        isDaysEqual(
          pharmacyBenefit.mailOrderSupplyDays,
          pharmacyBenefit.mailOrderSpecialityDrugDays,
        );
    } else if (
      isRetailExist &&
      pharmacyBenefit.retailSupplyDays &&
      pharmacyBenefit.retailSpecialityDrugDays
    ) {
      isSameDaySupply = isDaysEqual(
        pharmacyBenefit.retailSupplyDays,
        pharmacyBenefit.retailSpecialityDrugDays,
      );
    } else if (
      isMailOrderExist &&
      pharmacyBenefit.mailOrderSupplyDays &&
      pharmacyBenefit.mailOrderSpecialityDrugDays
    ) {
      isSameDaySupply = isDaysEqual(
        pharmacyBenefit.mailOrderSupplyDays,
        pharmacyBenefit.mailOrderSpecialityDrugDays,
      );
    }
  }
  return isSameDaySupply;
};

const networkSortOrder = [
  'PREFFERED',
  'Client Preferred Level 1 Network',
  'Client Preferred Level 2 Network',
  'Client Non-preferred Network',
  'INN',
  'DISINCENTIVIZED',
  'OON',
];

export const uniqueNetworksFromVariation = (
  variation: Variation | DentalPlanService,
): AccumulationNetwork[] => {
  const benefits = Object.keys(variation?.benefits);
  return benefits
    ? flatten(
        benefits.map(
          (key: string): Accumulation[] =>
            variation.benefits[key]?.accumulations as Accumulation[],
        ),
      )
        .map((accum: Accumulation) => ({
          networkType:
            accum.networkTierDisplayOrderCdDescription || accum.networkType,
          tier: accum.tier || '',
          networkTierDisplayOrderCdDescription:
            accum.networkTierDisplayOrderCdDescription,
        }))
        .filter(
          (
            value: AccumulationNetwork,
            index: number,
            self: AccumulationNetwork[],
          ) =>
            self.findIndex(
              (v) =>
                v.networkType === value.networkType &&
                (v.tier === value.tier ||
                  v.tier + value.tier === tierValues.TIER5),
            ) === index,
        )
        .slice()
        .sort(
          (a, b) =>
            networkSortOrder.indexOf(a.networkType) -
            networkSortOrder.indexOf(b.networkType),
        )
    : [];
};
